/**
 * Splitting the match list into the sections a scorer actually looks for.
 *
 * A flat reverse-chronological list is fine at five matches and useless at fifty: the one
 * being looked for is nearly always this week's, and a heading is what lets the eye stop
 * scanning. "Recent" and "Earlier" are the whole of it — anything finer would be a date
 * grouping, and a scorer does not remember which Sunday.
 *
 * Here rather than inside `Home.tsx` because it is the only part of that screen with an
 * answer that can be wrong. A card either renders or it does not; a boundary at seven days
 * has an off-by-one, a timezone and a clock-skew case, and none of those are reachable from
 * a component test.
 */
import type { MatchListItem } from "@sportsos/engine";

/** The window "Recent" covers. Seven days, per the UI PRD §4.1. */
const RECENT_MS = 7 * 24 * 60 * 60 * 1000;

export interface MatchGroups {
  readonly recent: readonly MatchListItem[];
  readonly earlier: readonly MatchListItem[];
}

/**
 * Split by how long ago each match was last touched.
 *
 * `now` is passed in rather than read from the clock, so the boundary is testable without
 * freezing time — the same reason `setupClock` and `scoringObserver` take one.
 *
 * Order within each group is the server's, untouched. `GET /api/matches` already returns
 * newest first, and re-sorting here would be this client inventing an order the public page
 * and any future surface would then have to guess at.
 */
export function groupMatches(
  matches: readonly MatchListItem[],
  now: number,
): MatchGroups {
  const recent: MatchListItem[] = [];
  const earlier: MatchListItem[] = [];

  for (const match of matches) {
    const at = Date.parse(match.updatedAt);
    // `Number.isNaN` catches a timestamp the server sent in a shape we do not understand.
    // Such a match goes to "Earlier" rather than being dropped: a match missing from the
    // list is indistinguishable from a match that was lost, which is the one impression
    // this product cannot afford to give.
    //
    // A *future* timestamp — a phone whose clock is behind the server's — lands in
    // "Recent" by the same arithmetic, which is where a match scored minutes ago belongs.
    (!Number.isNaN(at) && now - at <= RECENT_MS ? recent : earlier).push(match);
  }

  return { recent, earlier };
}
